import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { 
  Image, Plus, Trash2, Edit2, Eye, EyeOff, Save, X, 
  ArrowUp, ArrowDown, Upload, Check, AlertCircle 
} from 'lucide-react';

/**
 * ========================================
 * HOME BANNER MANAGER - ADMIN
 * ========================================
 * Allows admin to manage homepage banners
 */

const HomeBannerManager = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    image_url: '',
    is_active: false,
    link_type: 'categories',
    link_value: ''
  });

  // Fetch banners
  const fetchBanners = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('home_banners')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      setBanners(data || []);
    } catch (err) {
      console.error('Error fetching banners:', err);
      setError('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      setError('');

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `banner_${Date.now()}.${fileExt}`;
      const filePath = `banners/${fileName}`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, image_url: publicUrl }));
      setSuccess('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload image: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // Open modal for new banner
  const handleAddBanner = () => {
    setEditingBanner(null);
    setFormData({
      name: '',
      image_url: '',
      is_active: false,
      link_type: 'categories',
      link_value: ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Open modal for editing
  const handleEditBanner = (banner) => {
    setEditingBanner(banner);
    setFormData({
      name: banner.name,
      image_url: banner.image_url,
      is_active: banner.is_active,
      link_type: banner.link_type || 'categories',
      link_value: banner.link_value || ''
    });
    setShowModal(true);
    setError('');
    setSuccess('');
  };

  // Save banner
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a banner name');
      return;
    }
    if (!formData.image_url.trim()) {
      setError('Please upload or enter an image URL');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingBanner) {
        // Update existing
        const { error } = await supabase
          .from('home_banners')
          .update({
            name: formData.name,
            image_url: formData.image_url,
            is_active: formData.is_active,
            link_type: formData.link_type,
            link_value: formData.link_value,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingBanner.id);

        if (error) throw error;
        setSuccess('Banner updated successfully');
      } else {
        // Create new
        const maxOrder = banners.length > 0 
          ? Math.max(...banners.map(b => b.display_order || 0)) 
          : 0;

        const { error } = await supabase
          .from('home_banners')
          .insert({
            name: formData.name,
            image_url: formData.image_url,
            is_active: formData.is_active,
            link_type: formData.link_type,
            link_value: formData.link_value,
            display_order: maxOrder + 1
          });

        if (error) throw error;
        setSuccess('Banner created successfully');
      }

      fetchBanners();
      setTimeout(() => setShowModal(false), 1000);
    } catch (err) {
      console.error('Save error:', err);
      setError('Failed to save banner: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Toggle active status
  const handleToggleActive = async (banner) => {
    try {
      const { error } = await supabase
        .from('home_banners')
        .update({ 
          is_active: !banner.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', banner.id);

      if (error) throw error;
      fetchBanners();
    } catch (err) {
      console.error('Toggle error:', err);
      setError('Failed to update banner status');
    }
  };

  // Delete banner
  const handleDelete = async (banner) => {
    if (!window.confirm(`Delete banner "${banner.name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('home_banners')
        .delete()
        .eq('id', banner.id);

      if (error) throw error;
      fetchBanners();
      setSuccess('Banner deleted');
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete banner');
    }
  };

  // Move banner up/down
  const handleMove = async (banner, direction) => {
    const currentIndex = banners.findIndex(b => b.id === banner.id);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= banners.length) return;

    const otherBanner = banners[newIndex];

    try {
      // Swap display_order values
      await supabase
        .from('home_banners')
        .update({ display_order: otherBanner.display_order })
        .eq('id', banner.id);

      await supabase
        .from('home_banners')
        .update({ display_order: banner.display_order })
        .eq('id', otherBanner.id);

      fetchBanners();
    } catch (err) {
      console.error('Move error:', err);
      setError('Failed to reorder banners');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Home Banners</h1>
          <p className="text-gray-600 mt-1">Manage homepage banner images for the customer app</p>
        </div>
        <button
          onClick={handleAddBanner}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus size={20} />
          Add Banner
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {error}
          <button onClick={() => setError('')} className="ml-auto">
            <X size={18} />
          </button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <Check size={20} />
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto">
            <X size={18} />
          </button>
        </div>
      )}

      {/* Banners List */}
      {banners.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <Image size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No banners yet</h3>
          <p className="text-gray-600 mb-4">Add your first banner to display on the customer app homepage</p>
          <button
            onClick={handleAddBanner}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Add Your First Banner
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {banners.map((banner, index) => (
            <div
              key={banner.id}
              className={`bg-white rounded-xl shadow-sm overflow-hidden border-2 transition-colors ${
                banner.is_active ? 'border-green-500' : 'border-transparent'
              }`}
            >
              <div className="flex items-stretch">
                {/* Banner Image Preview */}
                <div className="w-80 h-40 flex-shrink-0 bg-gray-100">
                  <img
                    src={banner.image_url}
                    alt={banner.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/320x160?text=Image+Error';
                    }}
                  />
                </div>

                {/* Banner Info */}
                <div className="flex-1 p-4 flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{banner.name}</h3>
                      {banner.is_active && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      Links to: {banner.link_type === 'categories' ? 'Categories Page' : banner.link_type}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleActive(banner)}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        banner.is_active
                          ? 'bg-green-100 text-green-700 hover:bg-green-200'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {banner.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                      {banner.is_active ? 'Active' : 'Inactive'}
                    </button>

                    <button
                      onClick={() => handleEditBanner(banner)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors"
                    >
                      <Edit2 size={16} />
                      Edit
                    </button>

                    <button
                      onClick={() => handleDelete(banner)}
                      className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>

                    <div className="ml-auto flex items-center gap-1">
                      <button
                        onClick={() => handleMove(banner, 'up')}
                        disabled={index === 0}
                        className="p-1.5 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowUp size={16} />
                      </button>
                      <button
                        onClick={() => handleMove(banner, 'down')}
                        disabled={index === banners.length - 1}
                        className="p-1.5 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <ArrowDown size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingBanner ? 'Edit Banner' : 'Add New Banner'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Error in modal */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}
              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
                  {success}
                </div>
              )}

              {/* Banner Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Monthly Shopping Banner"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Image *
                </label>
                
                {/* Current Image Preview */}
                {formData.image_url && (
                  <div className="mb-4">
                    <img
                      src={formData.image_url}
                      alt="Banner preview"
                      className="w-full h-48 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/800x200?text=Invalid+Image+URL';
                      }}
                    />
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex gap-4">
                  <label className="flex-1">
                    <div className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors">
                      {uploading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-500"></div>
                      ) : (
                        <Upload size={20} className="text-gray-500" />
                      )}
                      <span className="text-gray-600">
                        {uploading ? 'Uploading...' : 'Upload Image'}
                      </span>
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploading}
                    />
                  </label>
                </div>

                {/* Or enter URL */}
                <div className="mt-4">
                  <label className="block text-sm text-gray-600 mb-2">Or enter image URL:</label>
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData(prev => ({ ...prev, image_url: e.target.value }))}
                    placeholder="https://example.com/banner.png"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Active Status</p>
                  <p className="text-sm text-gray-600">Display this banner on the homepage</p>
                </div>
                <button
                  onClick={() => setFormData(prev => ({ ...prev, is_active: !prev.is_active }))}
                  className={`relative w-14 h-8 rounded-full transition-colors ${
                    formData.is_active ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                >
                  <div
                    className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-transform ${
                      formData.is_active ? 'translate-x-7' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <Save size={20} />
                )}
                {saving ? 'Saving...' : 'Save Banner'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeBannerManager;
